// File open/save/export/import, recent files, place embedded
import type { EditorState } from "./state/EditorState";
import type { RasterLayer, GradientFillLayer, SolidFillLayer, PatternFillLayer, BlendMode, SmartObjectLayer } from "../core/document/Layer";
import { PixelBuffer } from "../core/pixel/PixelBuffer";
import { DEFAULT_SMART_OBJECT_TRANSFORM } from "../core/document/Layer";
import { NewLayerCommand } from "../core/commands/NewLayerCommand";
import { decodeImage } from "../app/io/decodeImage";
import { parseSoCoDescriptor, parseGdFlDescriptor, parsePtFlDescriptor, parseAdjustmentFromInfo } from "../core/io/psd/parseAdjustmentFill";
import { downloadBlob, exportDocument, exportBmp, exportGif, exportIco, exportSvg } from "../app/io/encodeImage";
import { encodeTiff } from "../app/io/decodeImage";

/** Map PSD 4-char blend-mode keys to our internal BlendMode strings. */
const PSD_BLEND_MAP: Record<string, BlendMode> = {
  "norm": "normal", "pass": "normal",
  "diss": "dissolve",
  "dark": "darken", "mul ": "multiply", "idiv": "color-burn", "lbrn": "linear-burn", "dkCl": "darker-color",
  "lite": "lighten", "scrn": "screen", "div ": "color-dodge", "lddg": "linear-dodge", "lgCl": "lighter-color",
  "over": "overlay", "sLit": "soft-light", "hLit": "hard-light", "vLit": "vivid-light", "lLit": "linear-light", "pLit": "pin-light", "hMix": "hard-mix",
  "diff": "difference", "smud": "exclusion", "fsub": "subtract", "fdiv": "divide",
  "hue ": "hue", "sat ": "saturation", "colr": "color", "lum ": "luminosity",
};
function blendFromPsd(key: string): BlendMode {
  return PSD_BLEND_MAP[key] ?? "normal";
}

export interface FileOpsDeps {
  state: EditorState;
  renderer: any;
  canvas: HTMLCanvasElement;
}

export function getRecentFiles(): string[] {
  try {
    const raw = localStorage.getItem("pixelpa-recent-files");
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

export function addRecentFile(path: string): void {
  try {
    const files = getRecentFiles();
    const idx = files.indexOf(path);
    if (idx >= 0) files.splice(idx, 1);
  files.unshift(path);
    if (files.length > 10) files.length = 10;
    localStorage.setItem("pixelpa-recent-files", JSON.stringify(files));
  } catch { /* ignore */ }
}

/**
 * Additional-info keys that identify adjustment layers.
 * Photoshop does not always emit `AdjL` for legacy adjustment layers (#245).
 */
const ADJ_KEYS = new Set([
  "brit", "levl", "curv", "hue2", "hue ", "blnc", "blwh", "phfl", "PhFl",
  "mixr", "nvrt", "post", "thrs", "grdm", "selc", "vibA", "expA", "AdjL",
  "CgEd", // legacy brightness-contrast descriptor
]);

/**
 * Read the `iOpa` additional-info block fill-opacity (0–255) as a 0..1 float.
 * Returns 1 (fully opaque) if the block is absent or too short.
 */
function readIOpacity(psdLayer: any): number {
  const info: any[] = psdLayer.additionalInfo ?? [];
  const block = info.find((b: any) => b.key === "iOpa");
  if (!block || !block.data || block.data.length < 1) return 1;
  return (block.data[0] as number) / 255;
}

/** @internal exported for tests only */
export function buildLayerTree(decoded: { psdLayer: any; pixels: PixelBuffer; id: string }[]): any[] {
  const root: any[] = [];
  for (const entry of decoded) {
    const { psdLayer, pixels, id } = entry;
    const info: any[] = psdLayer.additionalInfo ?? [];
    const visible = !(psdLayer.flags & 0x02);
    const opacity = psdLayer.opacity / 255;
    const fillOpacity = readIOpacity(psdLayer);
    const blendMode = blendFromPsd(psdLayer.blendModeKey);
    const clippedToBelow = psdLayer.clipping === 1;
    const base = {
      id, visible, opacity, fillOpacity, blendMode, clippedToBelow,
      lockTransparency: false, lockImage: false, lockPosition: false, lockAll: false,
      name: psdLayer.name || "Layer",
    };

    // Fill layers: classify by GdFl / SoCo / PtFl additional-info key
    // (same logic as buildLayerTree in main.ts, #186).
    const hasSoCo = info.some((b: any) => b.key === "SoCo");
    const hasGdFl = info.some((b: any) => b.key === "GdFl");
    const hasPtFl = info.some((b: any) => b.key === "PtFl");
    // Adjustment layers: detect by known adj keys even without AdjL (#245).
    const hasAdj = info.some((b: any) => ADJ_KEYS.has(b.key));

    // Adjustment layers take precedence over fill (SoCo is also sometimes
    // present on adj layers in older PS versions).
    if (hasAdj && !hasSoCo && !hasGdFl && !hasPtFl) {
      const parsed = parseAdjustmentFromInfo(info as { key: string; data: Uint8Array }[]);
      const adjustment = parsed?.kind ?? "brightness-contrast";
      const params = parsed?.params ?? {};
      root.push({ ...base, kind: "adjustment", adjustment, params, x: 0, y: 0 });
      continue;
    }

    if (hasSoCo) {
      const block = info.find((b: any) => b.key === "SoCo");
      const parsed = block ? parseSoCoDescriptor(block.data) : null;
      const layer: SolidFillLayer = {
        ...base,
        kind: "solid-fill",
        color: parsed
          ? { r: parsed.color.r, g: parsed.color.g, b: parsed.color.b, a: parsed.color.a }
          : { r: 255, g: 255, b: 255, a: 1 },
        x: 0, y: 0,
      };
      root.push(layer);
      continue;
    }

    if (hasGdFl) {
      const block = info.find((b: any) => b.key === "GdFl");
      const parsed = block ? parseGdFlDescriptor(block.data) : null;
      const layer: GradientFillLayer = {
        ...base,
        kind: "gradient-fill",
        gradientType: parsed?.gradientType ?? "linear",
        angle: parsed?.angle ?? 90,
        scale: parsed?.scale ?? 100,
        reverse: parsed?.reverse ?? false,
        dither: true,
        stops: parsed?.stops ?? [
          { position: 0, color: { r: 0, g: 0, b: 0, a: 1 } },
          { position: 1, color: { r: 255, g: 255, b: 255, a: 1 } },
        ],
        x: 0, y: 0,
      };
      root.push(layer);
      continue;
    }

    if (hasPtFl) {
      const block = info.find((b: any) => b.key === "PtFl");
      const parsed = block ? parsePtFlDescriptor(block.data) : null;
      const layer: PatternFillLayer = {
        ...base,
        kind: "pattern-fill",
        pattern: null,
        patternName: parsed?.patternName ?? "None",
        scale: parsed?.scale ?? 100,
        linkWithLayer: true,
        x: 0, y: 0,
      };
      root.push(layer);
      continue;
    }

    // Default: raster layer — use layer bounds for x/y so out-of-canvas
    // pixel buffers are positioned correctly.
    const layer: RasterLayer = {
      ...base,
      kind: "raster",
      x: psdLayer.left ?? 0,
      y: psdLayer.top ?? 0,
      pixels,
    };
    root.push(layer);
  }
  return root;
}

export async function savePngSilent(deps: FileOpsDeps): Promise<void> {
  if (!deps.state.doc) return;
  try {
    const blob = await exportDocument(deps.state.doc, "image/png");
    const base = deps.state.savedPath || deps.state.doc.name.replace(/\.[^.]+$/, "") || "Untitled";
    downloadBlob(blob, base.endsWith(".png") ? base : `${base}.png`);
    deps.state.markSaved();
  } catch (err) {
    console.error("Save failed:", err);
  }
}

export async function exportAs(deps: FileOpsDeps, format: string, quality: number): Promise<void> {
  if (!deps.state.doc) return;
  try {
    let blob: Blob;
    if (format === "bmp") {
      blob = await exportBmp(deps.state.doc);
    } else if (format === "gif") {
      blob = await exportGif(deps.state.doc);
    } else if (format === "ico") {
      blob = await exportIco(deps.state.doc);
    } else if (format === "tiff") {
      const canvas = document.createElement("canvas");
      canvas.width = deps.state.doc.width;
      canvas.height = deps.state.doc.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("2D context unavailable");
      // Composite layers...
      const imageData = ctx.getImageData(0, 0, deps.state.doc.width, deps.state.doc.height);
      const pixels = new PixelBuffer(deps.state.doc.width, deps.state.doc.height, "rgba8");
      pixels.data.set(imageData.data);
      blob = encodeTiff(pixels);
    } else if (format === "svg") {
      const svgStr = await exportSvg(deps.state.doc);
      blob = new Blob([svgStr], { type: "image/svg+xml" });
    } else {
      const mimeMap: Record<string, "image/png" | "image/jpeg" | "image/webp"> = { png: "image/png", jpeg: "image/jpeg", webp: "image/webp" };
      blob = await exportDocument(deps.state.doc, mimeMap[format] || "image/png", quality);
    }
    const ext = format === "jpeg" ? "jpg" : format;
    const base = deps.state.doc.name.replace(/\.[^.]+$/, "") || "Untitled";
    downloadBlob(blob, `${base}.${ext}`);
  } catch (err) {
    console.error("Export failed:", err);
  }
}

export async function placeEmbedded(deps: FileOpsDeps): Promise<void> {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/png,image/jpeg,image/webp,image/avif,image/gif,image/bmp,image/x-icon,.psd,.ico";
  input.style.display = "none";
  document.body.appendChild(input);
  input.addEventListener("change", async () => {
    const f = input.files?.[0];
    if (!f || !deps.state.doc) { input.remove(); return; }
    try {
      const { pixels, width, height } = await decodeImage(f);
      const doc = deps.state.doc;
      const layerName = f.name.replace(/\.[^.]+$/, "") || "Smart Object";
      const placedX = Math.round((doc.width - width) / 2);
      const placedY = Math.round((doc.height - height) / 2);
      // Embed the pixels as a raster layer inside an embedded document.
      const innerLayerId = crypto.randomUUID();
      const innerLayer: RasterLayer = {
        id: innerLayerId, kind: "raster", name: layerName,
        visible: true, opacity: 1, fillOpacity: 1, blendMode: "normal",
        x: 0, y: 0, lockTransparency: false, lockImage: false, lockPosition: false, lockAll: false, clippedToBelow: false,
        pixels,
      };
      const embedded = {
        name: layerName,
        width,
        height,
        dpi: 72,
        layers: [innerLayer],
        activeLayerId: innerLayerId,
        selection: null,
      };
      const soLayer: SmartObjectLayer = {
        id: crypto.randomUUID(), kind: "smart-object", name: layerName,
        visible: true, opacity: 1, fillOpacity: 1, blendMode: "normal",
        x: placedX, y: placedY,
        lockTransparency: false, lockImage: false, lockPosition: false, lockAll: false, clippedToBelow: false,
        embedded,
        transform: { ...DEFAULT_SMART_OBJECT_TRANSFORM },
        smartFilters: [],
      };
      deps.state.execute(new NewLayerCommand(soLayer));
      deps.state.requestRender();
    } catch (err) {
      console.error("Place failed:", err);
    }
    input.remove();
  });
  input.click();
}
